import React, { useState, useEffect } from 'react';
import { supabaseAdmin as supabase } from '../../lib/supabase.js';
import TimetableOverrideManager from './TimetableOverrideManager';
import TeacherAssignmentsManager from './TeacherAssignmentsManager';
import ParsingAlgorithmSettings from './ParsingAlgorithmSettings';
import { EnhancedTimetableParser } from '../Timetable/EnhancedTimetableParser';
import './SchoolTimetableSettings.css';

const SchoolTimetableSettings = ({ tenantId, schoolName, onBack }) => {
  const [activeTab, setActiveTab] = useState('global');
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  
  // Parsing test states
  const [adminPassword, setAdminPassword] = useState('');
  const [showParsingTest, setShowParsingTest] = useState(false);
  const [testFileContent, setTestFileContent] = useState('');
  const [parsingResult, setParsingResult] = useState(null);
  const [parsingError, setParsingError] = useState('');
  
  // Enhanced settings states
  const [teacherRoles, setTeacherRoles] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [classStructures, setClassStructures] = useState([]);
  const [electiveSubjects, setElectiveSubjects] = useState([]);
  const [combinedLessons, setCombinedLessons] = useState([]);
  const [coTeachingArrangements, setCoTeachingArrangements] = useState([]);
  const [timetablePlans, setTimetablePlans] = useState([]);

  // Default configuration for this tenant/school
  const defaultConfig = {
    tenant_id: tenantId,
    school_name: schoolName,
    academic_year: "2024-2025",
    total_periods: 15,
    days_of_week: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
    time_slots: [
      { period_sequence: 1, time_range: "8:00-8:10", period_node: "班主任節", period_number: 0 },
      { period_sequence: 2, time_range: "8:10-8:45", period_node: "Period 1", period_number: 1 },
      { period_sequence: 3, time_range: "8:45-9:20", period_node: "Period 2", period_number: 2 },
      { period_sequence: 4, time_range: "9:20-9:55", period_node: "Period 3", period_number: 3 },
      { period_sequence: 5, time_range: "9:55-10:15", period_node: "Recess", period_number: 0 },
      { period_sequence: 6, time_range: "10:15-10:50", period_node: "Period 4", period_number: 4 },
      { period_sequence: 7, time_range: "10:50-11:25", period_node: "Period 5", period_number: 5 },
      { period_sequence: 8, time_range: "11:25-11:30", period_node: "Recess", period_number: 0 },
      { period_sequence: 9, time_range: "11:30-12:05", period_node: "Period 6", period_number: 6 },
      { period_sequence: 10, time_range: "12:05-12:40", period_node: "Period 7", period_number: 7 },
      { period_sequence: 11, time_range: "12:40-1:50", period_node: "Lunch", period_number: 0 },
      { period_sequence: 12, time_range: "1:50-2:25", period_node: "Period 8", period_number: 8 },
      { period_sequence: 13, time_range: "2:25-3:00", period_node: "Period 9", period_number: 9 },
      { period_sequence: 14, time_range: "3:00-3:35", period_node: "Period 10", period_number: 10 },
      { period_sequence: 15, time_range: "3:35-3:50", period_node: "班主任節", period_number: 0 }
    ],
    day_specific_overrides: {
      Friday: {
        period_14: {
          period_node: "班主任節",
          period_number: 0,
          note: "Friday special: Period 10 becomes 班主任節"
        }
      }
    }
  };

  useEffect(() => {
    loadConfiguration();
  }, [tenantId]);

  const loadConfiguration = async () => {
    try {
      setLoading(true);
      
      // Load configuration from existing table structure (without tenant_id for now)
      const { data, error } = await supabase
        .from('timetable_configurations')
        .select('*')
        .eq('academic_year', '2024-2025')
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Error loading configuration:', error);
        setConfig(defaultConfig);
      } else if (data) {
        console.log('✅ Configuration loaded:', data);
        setConfig(data.config);
      } else {
        setConfig(defaultConfig);
      }
    } catch (error) {
      console.error('Error loading configuration:', error);
      setConfig(defaultConfig);
    } finally {
      setLoading(false);
    }
  };

  const saveConfiguration = async () => {
    try {
      setSaving(true);
      
      // Update existing record with new config
      const { error } = await supabase
        .from('timetable_configurations')
        .update({
          config: config,
          updated_at: new Date().toISOString()
        })
        .eq('academic_year', '2024-2025');

      if (error) {
        throw error;
      }

      setMessage(`Timetable configuration saved for ${schoolName}!`);
      setMessageType('success');
    } catch (error) {
      console.error('Error saving configuration:', error);
      setMessage('Error saving configuration: ' + error.message);
      setMessageType('error');
    } finally {
      setSaving(false);
    }
  };

  const updateTimeSlot = (index, field, value) => {
    const newConfig = { ...config };
    newConfig.time_slots[index][field] = value;
    setConfig(newConfig);
  };

  const updateDayOverride = (day, period, field, value) => {
    const newConfig = { ...config };
    if (!newConfig.day_specific_overrides[day]) {
      newConfig.day_specific_overrides[day] = {};
    }
    if (!newConfig.day_specific_overrides[day][period]) {
      newConfig.day_specific_overrides[day][period] = {};
    }
    newConfig.day_specific_overrides[day][period][field] = value;
    setConfig(newConfig);
  };

  const resetToDefault = () => {
    setConfig(defaultConfig);
    setMessage('Configuration reset to default');
    setMessageType('info');
  };

  // Parsing test functions
  const handleAdminPasswordSubmit = (e) => {
    e.preventDefault();
    if (adminPassword === '88888888') {
      setShowParsingTest(true);
      setMessage('Admin access granted. Parsing test enabled.');
      setMessageType('success');
    } else {
      setMessage('Invalid admin password. Access denied.');
      setMessageType('error');
    }
  };

  const testParsing = () => {
    if (!testFileContent.trim()) {
      setMessage('Please enter timetable file content to test parsing.');
      setMessageType('error');
      return;
    }

    try {
      setParsingError('');
      setParsingResult(null);

      // Create setup data from current config
      const setupData = {
        mode: 'weekday',
        dayPeriods: config.total_periods || 15,
        periodPatterns: config.time_slots || [],
        day_specific_overrides: config.day_specific_overrides || {}
      };

      // Initialize parser with current settings
      const parser = new EnhancedTimetableParser(setupData);
      
      // Parse the test content
      const result = parser.parseTimetableFile(testFileContent);
      
      setParsingResult(result);
      setMessage('Parsing test completed successfully!');
      setMessageType('success');
    } catch (error) {
      console.error('Parsing test error:', error);
      setParsingError(error.message);
      setMessage('Parsing test failed: ' + error.message);
      setMessageType('error');
    }
  };

  const loadSampleFile = () => {
    const sampleContent = `## Teacher 43: 謝正祖 (Xie Zheng Zu)
**Class:** 祖 (Zu), **School Number:** 44

**Teaching Schedule:**

**Monday:**
- Period 1 (8:10-8:45): 1A 數學, Room 233
- Period 4 (10:15-10:50): 1B 科學, Room 308
- Period 5 (10:50-11:25): 1B 科學, Room 308
- Period 6 (11:30-12:05): 6D 數學, Room 208
- Period 7 (12:05-12:40): 6D 數學, Room 208
- Period 9 (2:25-3:00): 1C 數學, Room 231
- Period 10 (3:00-3:35): 1C 數學, Room 231

**Tuesday:**
- Period 2 (8:45-9:20): S6 數學同儕
- Period 3 (9:20-9:55): 6D 數學, Room 208
- Period 6 (11:30-12:05): 1A 數學, Room 233
- Period 7 (12:05-12:40): 1A 數學, Room 233
- Period 9 (2:25-3:00): 1C 數學, Room 231
- Period 10 (3:00-3:35): 1C 數學, Room 231

**Wednesday:**
- Period 1 (8:10-8:45): 早會/班主任節, 班主任
- Period 2 (8:45-9:20): 2C 科學, Room 310
- Period 3 (9:20-9:55): 2C 科學, Room 310
- Period 4 (10:15-10:50): 1A 數學, Room 233
- Period 5 (10:50-11:25): 1C 數學, Room 231
- Period 8 (1:50-2:25): 6D 數學, Room 208
- Period 9 (2:25-3:00): 6D 數學, Room 208
- Period 10 (3:00-3:35): 1B 科學, Room 308

**Thursday:**
- Period 1 (8:10-8:45): 6D 數學, Room 208
- Period 2 (8:45-9:20): 1A 數學, Room 233
- Period 3 (9:20-9:55): 1A 數學, Room 233
- Period 4 (10:15-10:50): 1C 數學, Room 231
- Period 5 (10:50-11:25): 1C 數學, Room 231
- Period 6 (11:30-12:05): S2 科學同儕
- Period 8 (1:50-2:25): S1科學同儕
- Period 10 (3:00-3:35): 1A 班主任節, Room 233 鄧志良

**Friday:**
- Period 1 (8:10-8:45): 2C 科學, Room 310
- Period 2 (8:45-9:20): 2C 科學, Room 310
- Period 3 (9:20-9:55): 6S 跨班自主學習節, Room 208/Hall
- Period 4 (10:15-10:50): 1C 數學, Room 231
- Period 5 (10:50-11:25): 6D數學, Room 208
- Period 8 (1:50-2:25): 2C 科學(上學期), Room 310
- Period 9 (2:25-3:00): 3B 科學(下學期), Room 308

**Teaching Load:** Mathematics (數學), Science (科學), Cross-class Self-directed Learning (跨班自主學習節)`;

    setTestFileContent(sampleContent);
    setMessage('Sample file loaded. You can now test parsing.');
    setMessageType('info');
  };

  if (loading) {
    return (
      <div className="school-timetable-settings">
        <div className="loading">Loading timetable configuration for {schoolName}...</div>
      </div>
    );
  }

  return (
    <div className="school-timetable-settings">
      <div className="settings-header">
        <div className="header-top">
          {onBack && (
            <button className="back-button" onClick={onBack}>
              ← 返回主頁
            </button>
          )}
          <h2>🏫 School Timetable Configuration</h2>
        </div>
        <p>Configure global timetable settings for <strong>{schoolName}</strong></p>
        <p className="tenant-info">Tenant ID: {tenantId}</p>
      </div>

      {message && (
        <div className={`message ${messageType}`}>
          {message}
        </div>
      )}

      {/* Tab Navigation */}
      <div className="tab-navigation">
        <button
          className={`tab ${activeTab === 'global' ? 'active' : ''}`}
          onClick={() => setActiveTab('global')}
        >
          🌐 Global Settings
        </button>
        <button
          className={`tab ${activeTab === 'overrides' ? 'active' : ''}`}
          onClick={() => setActiveTab('overrides')}
        >
          🎛️ Individual Overrides
        </button>
        <button
          className={`tab ${activeTab === 'parsing' ? 'active' : ''}`}
          onClick={() => setActiveTab('parsing')}
        >
          🔬 Parsing Test
        </button>
        <button
          className={`tab ${activeTab === 'algorithm' ? 'active' : ''}`}
          onClick={() => setActiveTab('algorithm')}
        >
          🔧 Parsing Algorithm
        </button>
        <button
          className={`tab ${activeTab === 'teachers' ? 'active' : ''}`}
          onClick={() => setActiveTab('teachers')}
        >
          👥 Teacher Roles
        </button>
        <button
          className={`tab ${activeTab === 'rooms' ? 'active' : ''}`}
          onClick={() => setActiveTab('rooms')}
        >
          🏢 Room Management
        </button>
        <button
          className={`tab ${activeTab === 'classes' ? 'active' : ''}`}
          onClick={() => setActiveTab('classes')}
        >
          📚 Class Structure
        </button>
        <button
          className={`tab ${activeTab === 'electives' ? 'active' : ''}`}
          onClick={() => setActiveTab('electives')}
        >
          🎓 Elective System
        </button>
        <button
          className={`tab ${activeTab === 'integration' ? 'active' : ''}`}
          onClick={() => setActiveTab('integration')}
        >
          🔗 Integration
        </button>
        <button
          className={`tab ${activeTab === 'planner' ? 'active' : ''}`}
          onClick={() => setActiveTab('planner')}
        >
          📋 Timetable Planner
        </button>
      </div>

      <div className="settings-content">
        {activeTab === 'global' ? (
          <>
            {/* School Information */}
        <div className="config-section">
          <h3>School Information</h3>
          <div className="school-info">
            <div className="info-item">
              <label>School Name:</label>
              <span>{schoolName}</span>
            </div>
            <div className="info-item">
              <label>Tenant ID:</label>
              <span>{tenantId}</span>
            </div>
            <div className="form-group">
              <label>Academic Year:</label>
              <input
                type="text"
                value={config.academic_year}
                onChange={(e) => setConfig({ ...config, academic_year: e.target.value })}
                className="form-input"
              />
            </div>
          </div>
        </div>

        {/* Time Slots Configuration */}
        <div className="config-section">
          <h3>Time Slots Configuration</h3>
          <p className="section-description">
            Configure the daily timetable structure for all teachers and classes in this school.
          </p>
          <div className="time-slots-table">
            <table>
              <thead>
                <tr>
                  <th>Period Sequence</th>
                  <th>Time Range</th>
                  <th>Period Node</th>
                  <th>Period Number</th>
                </tr>
              </thead>
              <tbody>
                {config.time_slots.map((slot, index) => (
                  <tr key={slot.period_sequence}>
                    <td>{slot.period_sequence}</td>
                    <td>
                      <input
                        type="text"
                        value={slot.time_range}
                        onChange={(e) => updateTimeSlot(index, 'time_range', e.target.value)}
                        className="form-input small"
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        value={slot.period_node}
                        onChange={(e) => updateTimeSlot(index, 'period_node', e.target.value)}
                        className="form-input small"
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        value={slot.period_number}
                        onChange={(e) => updateTimeSlot(index, 'period_number', parseInt(e.target.value))}
                        className="form-input small"
                        min="0"
                        max="10"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Day-Specific Overrides */}
        <div className="config-section">
          <h3>Day-Specific Overrides</h3>
          <p className="section-description">
            Configure special day patterns that differ from the standard schedule.
          </p>
          
          {/* Friday Special Case */}
          <div className="day-override">
            <h4>Friday Special Case</h4>
            <div className="override-controls">
              <label>
                <input
                  type="checkbox"
                  checked={config.day_specific_overrides.Friday?.period_14?.period_node === "班主任節"}
                  onChange={(e) => {
                    if (e.target.checked) {
                      updateDayOverride('Friday', 'period_14', 'period_node', '班主任節');
                      updateDayOverride('Friday', 'period_14', 'period_number', 0);
                    } else {
                      updateDayOverride('Friday', 'period_14', 'period_node', 'Period 10');
                      updateDayOverride('Friday', 'period_14', 'period_number', 10);
                    }
                  }}
                />
                Enable Friday special case (Period 10 = 班主任節)
              </label>
            </div>
            
            {config.day_specific_overrides.Friday?.period_14 && (
              <div className="override-details">
                <div className="form-group">
                  <label>Period Node:</label>
                  <input
                    type="text"
                    value={config.day_specific_overrides.Friday.period_14.period_node}
                    onChange={(e) => updateDayOverride('Friday', 'period_14', 'period_node', e.target.value)}
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label>Period Number:</label>
                  <input
                    type="number"
                    value={config.day_specific_overrides.Friday.period_14.period_number}
                    onChange={(e) => updateDayOverride('Friday', 'period_14', 'period_number', parseInt(e.target.value))}
                    className="form-input"
                    min="0"
                    max="10"
                  />
                </div>
                <div className="form-group">
                  <label>Note:</label>
                  <input
                    type="text"
                    value={config.day_specific_overrides.Friday.period_14.note}
                    onChange={(e) => updateDayOverride('Friday', 'period_14', 'note', e.target.value)}
                    className="form-input"
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="action-buttons">
          <button
            onClick={saveConfiguration}
            disabled={saving}
            className="btn btn-primary"
          >
            {saving ? 'Saving...' : 'Save School Configuration'}
          </button>
          <button
            onClick={resetToDefault}
            className="btn btn-secondary"
          >
            Reset to Default
          </button>
        </div>

            {/* Impact Information */}
            <div className="config-section impact-info">
              <h3>Configuration Impact</h3>
              <div className="impact-details">
                <p>✅ <strong>All Teachers:</strong> Will use this timetable structure</p>
                <p>✅ <strong>All Classes:</strong> Will follow this schedule</p>
                <p>✅ <strong>All Students:</strong> Will see this timetable format</p>
                <p>✅ <strong>School-wide:</strong> Changes apply to entire school</p>
              </div>
            </div>
          </>
        ) : activeTab === 'parsing' ? (
          <div className="parsing-test-section">
            <h2>🔬 Timetable Parsing Test</h2>
            <p>Test the parsing algorithm with sample timetable files to verify settings configuration.</p>
            
            {!showParsingTest ? (
              <div className="admin-access">
                <h3>🔐 Admin Access Required</h3>
                <p>Enter admin password to access parsing test tools:</p>
                <form onSubmit={handleAdminPasswordSubmit}>
                  <input
                    type="password"
                    value={adminPassword}
                    onChange={(e) => setAdminPassword(e.target.value)}
                    placeholder="Enter admin password"
                    className="admin-password-input"
                  />
                  <button type="submit" className="admin-submit-btn">
                    🔓 Access Parsing Test
                  </button>
                </form>
              </div>
            ) : (
              <div className="parsing-test-content">
                <div className="test-controls">
                  <h3>📝 Test File Content</h3>
                  <div className="file-input-section">
                    <button 
                      onClick={loadSampleFile}
                      className="load-sample-btn"
                    >
                      📄 Load Sample File (謝正祖.txt)
                    </button>
                    <textarea
                      value={testFileContent}
                      onChange={(e) => setTestFileContent(e.target.value)}
                      placeholder="Paste timetable file content here..."
                      className="file-content-textarea"
                      rows={15}
                    />
                  </div>
                  
                  <div className="test-actions">
                    <button 
                      onClick={testParsing}
                      className="test-parsing-btn"
                      disabled={!testFileContent.trim()}
                    >
                      🧪 Test Parsing
                    </button>
                    <button 
                      onClick={() => {
                        setTestFileContent('');
                        setParsingResult(null);
                        setParsingError('');
                      }}
                      className="clear-btn"
                    >
                      🗑️ Clear
                    </button>
                  </div>
                </div>

                {parsingError && (
                  <div className="parsing-error">
                    <h3>❌ Parsing Error</h3>
                    <pre className="error-content">{parsingError}</pre>
                  </div>
                )}

                {parsingResult && (
                  <div className="parsing-result">
                    <h3>✅ Parsing Result</h3>
                    <div className="result-summary">
                      <p><strong>Teacher:</strong> {parsingResult.teacher}</p>
                      <p><strong>Days Parsed:</strong> {Object.keys(parsingResult.schedule).length}</p>
                    </div>
                    
                    <div className="result-details">
                      <h4>📊 Detailed Results by Day:</h4>
                      {Object.entries(parsingResult.schedule).map(([day, periods]) => (
                        <div key={day} className="day-result">
                          <h5>{day.charAt(0).toUpperCase() + day.slice(1)}</h5>
                          <div className="periods-list">
                            {periods.map((period, index) => (
                              <div key={index} className="period-item">
                                <span className="period-info">
                                  Period {period.period} (Seq: {period.period_sequence})
                                </span>
                                <span className="period-node">{period.period_node}</span>
                                <span className="period-subject">{period.subject}</span>
                                {period.classes && period.classes.length > 0 && (
                                  <span className="period-classes">Classes: {period.classes.join(', ')}</span>
                                )}
                                {period.rooms && period.rooms.length > 0 && (
                                  <span className="period-rooms">Room: {period.rooms.join(', ')}</span>
                                )}
                                {period.teachers && period.teachers.length > 0 && (
                                  <span className="period-teachers">Teacher: {period.teachers.join(', ')}</span>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    <div className="raw-result">
                      <h4>🔍 Raw JSON Result:</h4>
                      <pre className="json-content">
                        {JSON.stringify(parsingResult, null, 2)}
                      </pre>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        ) : activeTab === 'planner' ? (
          <div className="timetable-planner-section">
            <h2>📋 Timetable Planner Integration</h2>
            <p>Plan and configure timetable structure for academic year planning and migration.</p>
            
            {!showParsingTest ? (
              <div className="admin-access">
                <h3>🔐 Admin Access Required</h3>
                <p>Enter admin password to access Timetable Planner tools:</p>
                <form onSubmit={handleAdminPasswordSubmit}>
                  <input
                    type="password"
                    value={adminPassword}
                    onChange={(e) => setAdminPassword(e.target.value)}
                    placeholder="Enter admin password"
                    className="admin-password-input"
                  />
                  <button type="submit" className="admin-submit-btn">
                    🔓 Access Timetable Planner
                  </button>
                </form>
              </div>
            ) : (
              <div className="planner-content">
                <div className="planner-overview">
                  <h3>🎯 Planner Overview</h3>
                  <p>The Timetable Planner module helps you plan and configure timetable structures for new academic years. It integrates seamlessly with the Timetable module for daily operations.</p>
                  
                  <div className="planner-features">
                    <div className="feature-card">
                      <h4>📅 Academic Year Planning</h4>
                      <ul>
                        <li>Create new academic year configurations</li>
                        <li>Define 15-period timetable structure</li>
                        <li>Configure time slots and special periods</li>
                        <li>Set up flexible "bar" system</li>
                      </ul>
                    </div>
                    
                    <div className="feature-card">
                      <h4>👨‍🏫 Class Role Assignment</h4>
                      <ul>
                        <li>Assign Class Masters to classes</li>
                        <li>Assign Vice Class Masters as backup</li>
                        <li>Automatic 班主任節 period assignment</li>
                        <li>Role detection from teacher files</li>
                      </ul>
                    </div>
                    
                    <div className="feature-card">
                      <h4>🔄 Migration Tools</h4>
                      <ul>
                        <li>Export configuration to Settings</li>
                        <li>Import from previous academic year</li>
                        <li>Validate configuration before migration</li>
                        <li>One-click deployment to Timetable module</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="planner-workflow">
                  <h3>🔄 Integration Workflow</h3>
                  <div className="workflow-steps">
                    <div className="workflow-step">
                      <div className="step-number">1</div>
                      <div className="step-content">
                        <h4>Timetable Planner</h4>
                        <p>Plan resources, assign teachers to classes, configure timetable structure</p>
                      </div>
                    </div>
                    
                    <div className="workflow-arrow">→</div>
                    
                    <div className="workflow-step">
                      <div className="step-number">2</div>
                      <div className="step-content">
                        <h4>Settings Configuration</h4>
                        <p>Import planner data, configure global settings, test configuration</p>
                      </div>
                    </div>
                    
                    <div className="workflow-arrow">→</div>
                    
                    <div className="workflow-step">
                      <div className="step-number">3</div>
                      <div className="step-content">
                        <h4>Timetable Module</h4>
                        <p>Daily operations, teacher file parsing, student timetables</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="planner-tools">
                  <h3>🛠️ Planner Tools</h3>
                  <div className="tools-grid">
                    <div className="tool-card">
                      <h4>📊 Class Role Detection</h4>
                      <p>Test class role detection with sample teacher files</p>
                      <button 
                        onClick={() => {
                          setTestFileContent(`## Teacher 1: John Doe
**Class Master:** 1A
**Vice Class Master:** 1B

**Monday:**
- Period 1 (8:10-8:45): 1A Mathematics, Room 101
- Period 2 (8:45-9:20): 1B Science, Room 102`);
                          setActiveTab('parsing');
                        }}
                        className="tool-btn"
                      >
                        🧪 Test Role Detection
                      </button>
                    </div>
                    
                    <div className="tool-card">
                      <h4>📋 Configuration Export</h4>
                      <p>Export current settings for academic year planning</p>
                      <button 
                        onClick={() => {
                          const exportData = {
                            academic_year: config?.academic_year || '2024-2025',
                            timetable_structure: config?.time_slots || [],
                            total_periods: config?.total_periods || 15,
                            day_specific_overrides: config?.day_specific_overrides || {}
                          };
                          
                          const dataStr = JSON.stringify(exportData, null, 2);
                          const dataBlob = new Blob([dataStr], {type: 'application/json'});
                          const url = URL.createObjectURL(dataBlob);
                          const link = document.createElement('a');
                          link.href = url;
                          link.download = `timetable-config-${exportData.academic_year}.json`;
                          link.click();
                          URL.revokeObjectURL(url);
                          
                          setMessage('Configuration exported successfully!');
                          setMessageType('success');
                        }}
                        className="tool-btn"
                      >
                        📥 Export Configuration
                      </button>
                    </div>
                    
                    <div className="tool-card">
                      <h4>📖 Integration Guide</h4>
                      <p>View detailed integration guide and best practices</p>
                      <button 
                        onClick={() => {
                          window.open('/TIMETABLE_PLANNER_INTEGRATION_GUIDE.md', '_blank');
                        }}
                        className="tool-btn"
                      >
                        📚 View Guide
                      </button>
                    </div>
                  </div>
                </div>

                <div className="planner-status">
                  <h3>📈 Current Status</h3>
                  <div className="status-grid">
                    <div className="status-item">
                      <span className="status-label">Academic Year:</span>
                      <span className="status-value">{config?.academic_year || 'Not Set'}</span>
                    </div>
                    <div className="status-item">
                      <span className="status-label">Total Periods:</span>
                      <span className="status-value">{config?.total_periods || 0}</span>
                    </div>
                    <div className="status-item">
                      <span className="status-label">Time Slots:</span>
                      <span className="status-value">{config?.time_slots?.length || 0}</span>
                    </div>
                    <div className="status-item">
                      <span className="status-label">Day Overrides:</span>
                      <span className="status-value">{Object.keys(config?.day_specific_overrides || {}).length}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : activeTab === 'algorithm' ? (
          <ParsingAlgorithmSettings 
            tenantId={tenantId}
            schoolName={schoolName}
            onBack={() => setActiveTab('global')}
          />
        ) : activeTab === 'teachers' ? (
          <TeacherAssignmentsManager 
            tenantId={tenantId}
            onTeacherSelect={(teacher) => {
              console.log('Selected teacher:', teacher);
              // You can add additional logic here if needed
            }}
          />
        ) : activeTab === 'rooms' ? (
          <div className="room-management-section">
            <h2>🏢 Room Management</h2>
            <p>Manage school rooms, equipment, and capacity.</p>
            
            <div className="rooms-overview">
              <h3>📊 Room Inventory</h3>
              <div className="rooms-summary">
                <div className="summary-card">
                  <h4>Total Rooms</h4>
                  <span className="count">{rooms.length}</span>
                </div>
                <div className="summary-card">
                  <h4>Classrooms</h4>
                  <span className="count">{rooms.filter(r => r.room_type === 'classroom').length}</span>
                </div>
                <div className="summary-card">
                  <h4>Labs</h4>
                  <span className="count">{rooms.filter(r => r.room_type === 'lab').length}</span>
                </div>
                <div className="summary-card">
                  <h4>Special Rooms</h4>
                  <span className="count">{rooms.filter(r => r.room_type === 'special').length}</span>
                </div>
              </div>
            </div>

            <div className="add-room-section">
              <h3>➕ Add New Room</h3>
              <div className="add-room-form">
                <div className="form-row">
                  <input type="text" placeholder="Room Code (e.g., R001)" className="form-input" />
                  <input type="text" placeholder="Room Name" className="form-input" />
                </div>
                <div className="form-row">
                  <select className="form-select">
                    <option value="">Select Room Type</option>
                    <option value="classroom">Classroom</option>
                    <option value="lab">Laboratory</option>
                    <option value="hall">Hall</option>
                    <option value="library">Library</option>
                    <option value="special">Special Room</option>
                  </select>
                  <input type="number" placeholder="Capacity" className="form-input" />
                </div>
                <div className="form-row">
                  <input type="text" placeholder="Building" className="form-input" />
                  <input type="number" placeholder="Floor" className="form-input" />
                </div>
                <button className="add-room-btn">➕ Add Room</button>
              </div>
            </div>

            <div className="rooms-list">
              <h3>📋 Room List</h3>
              <div className="rooms-table">
                <div className="table-header">
                  <span>Code</span>
                  <span>Name</span>
                  <span>Type</span>
                  <span>Capacity</span>
                  <span>Location</span>
                  <span>Actions</span>
                </div>
                {rooms.map((room, index) => (
                  <div key={index} className="table-row">
                    <span className="room-code">{room.room_code}</span>
                    <span>{room.room_name}</span>
                    <span className="room-type-badge">{room.room_type}</span>
                    <span>{room.capacity || '-'}</span>
                    <span>{room.building} F{room.floor}</span>
                    <span className="actions">
                      <button className="edit-btn">✏️</button>
                      <button className="delete-btn">🗑️</button>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : activeTab === 'classes' ? (
          <div className="class-structure-section">
            <h2>📚 Class Structure Management</h2>
            <p>Configure class combinations, co-teaching arrangements, and combined lessons.</p>
            
            <div className="class-overview">
              <h3>📊 Class Structure Overview</h3>
              <div className="class-summary">
                <div className="summary-card">
                  <h4>Total Classes</h4>
                  <span className="count">{classStructures.length}</span>
                </div>
                <div className="summary-card">
                  <h4>Form 1-3</h4>
                  <span className="count">{classStructures.filter(c => c.form_level <= 3).length}</span>
                </div>
                <div className="summary-card">
                  <h4>Form 4-6</h4>
                  <span className="count">{classStructures.filter(c => c.form_level >= 4).length}</span>
                </div>
                <div className="summary-card">
                  <h4>Elective Classes</h4>
                  <span className="count">{classStructures.filter(c => c.class_code.includes('X')).length}</span>
                </div>
              </div>
            </div>

            <div className="class-management-tabs">
              <button className="sub-tab active">Class Structure</button>
              <button className="sub-tab">Combined Lessons</button>
              <button className="sub-tab">Co-teaching</button>
            </div>

            <div className="class-content">
              <h3>➕ Add New Class</h3>
              <div className="add-class-form">
                <div className="form-row">
                  <select className="form-select">
                    <option value="">Select Form Level</option>
                    <option value="1">Form 1</option>
                    <option value="2">Form 2</option>
                    <option value="3">Form 3</option>
                    <option value="4">Form 4</option>
                    <option value="5">Form 5</option>
                    <option value="6">Form 6</option>
                  </select>
                  <input type="text" placeholder="Class Code (e.g., 1A, 4X1)" className="form-input" />
                </div>
                <div className="form-row">
                  <input type="text" placeholder="Class Name" className="form-input" />
                  <select className="form-select">
                    <option value="">Select Class Master</option>
                    {/* Teachers will be loaded here */}
                  </select>
                </div>
                <div className="form-row">
                  <input type="number" placeholder="Student Count" className="form-input" />
                  <input type="text" placeholder="Elective Groups (JSON)" className="form-input" />
                </div>
                <button className="add-class-btn">➕ Add Class</button>
              </div>
            </div>
          </div>
        ) : activeTab === 'electives' ? (
          <div className="elective-system-section">
            <h2>🎓 Elective System Management</h2>
            <p>Configure X1, X2 elective subjects and cross-class arrangements.</p>
            
            <div className="elective-overview">
              <h3>📊 Elective System Overview</h3>
              <div className="elective-summary">
                <div className="summary-card">
                  <h4>Total Electives</h4>
                  <span className="count">{electiveSubjects.length}</span>
                </div>
                <div className="summary-card">
                  <h4>X1 Electives</h4>
                  <span className="count">{electiveSubjects.filter(e => e.elective_group === 'X1').length}</span>
                </div>
                <div className="summary-card">
                  <h4>X2 Electives</h4>
                  <span className="count">{electiveSubjects.filter(e => e.elective_group === 'X2').length}</span>
                </div>
                <div className="summary-card">
                  <h4>Core Electives</h4>
                  <span className="count">{electiveSubjects.filter(e => e.is_core_elective).length}</span>
                </div>
              </div>
            </div>

            <div className="elective-management">
              <h3>➕ Add Elective Subject</h3>
              <div className="add-elective-form">
                <div className="form-row">
                  <input type="text" placeholder="Subject Code" className="form-input" />
                  <input type="text" placeholder="Subject Name" className="form-input" />
                </div>
                <div className="form-row">
                  <select className="form-select">
                    <option value="">Select Form Level</option>
                    <option value="4">Form 4</option>
                    <option value="5">Form 5</option>
                    <option value="6">Form 6</option>
                  </select>
                  <select className="form-select">
                    <option value="">Select Elective Group</option>
                    <option value="X1">X1 Elective</option>
                    <option value="X2">X2 Elective</option>
                    <option value="X3">X3 Elective</option>
                  </select>
                </div>
                <div className="form-row">
                  <select className="form-select">
                    <option value="">Select Teacher</option>
                    {/* Teachers will be loaded here */}
                  </select>
                  <select className="form-select">
                    <option value="">Select Room</option>
                    {/* Rooms will be loaded here */}
                  </select>
                </div>
                <div className="form-row">
                  <input type="number" placeholder="Max Students" className="form-input" />
                  <label className="checkbox-label">
                    <input type="checkbox" />
                    Core Elective (Required)
                  </label>
                </div>
                <button className="add-elective-btn">➕ Add Elective</button>
              </div>
            </div>
          </div>
        ) : activeTab === 'integration' ? (
          <div className="integration-section">
            <h2>🔗 Timetable Planner Integration</h2>
            <p>Connect with Timetable Planner for academic year planning and migration.</p>
            
            <div className="integration-overview">
              <h3>📊 Integration Status</h3>
              <div className="integration-status">
                <div className="status-card">
                  <h4>Planner Connection</h4>
                  <span className="status-indicator success">✅ Connected</span>
                </div>
                <div className="status-card">
                  <h4>Last Migration</h4>
                  <span className="status-text">2024-08-15 14:30</span>
                </div>
                <div className="status-card">
                  <h4>Active Plans</h4>
                  <span className="count">{timetablePlans.filter(p => p.plan_status === 'approved').length}</span>
                </div>
              </div>
            </div>

            <div className="migration-tools">
              <h3>🔄 Migration Tools</h3>
              <div className="migration-actions">
                <button className="migration-btn export">
                  📤 Export to Planner
                </button>
                <button className="migration-btn import">
                  📥 Import from Planner
                </button>
                <button className="migration-btn validate">
                  ✅ Validate Settings
                </button>
                <button className="migration-btn sync">
                  🔄 Sync Data
                </button>
              </div>
            </div>

            <div className="planner-plans">
              <h3>📋 Timetable Plans</h3>
              <div className="plans-list">
                {timetablePlans.map((plan, index) => (
                  <div key={index} className="plan-card">
                    <div className="plan-header">
                      <h4>{plan.plan_name}</h4>
                      <span className={`plan-status ${plan.plan_status}`}>
                        {plan.plan_status}
                      </span>
                    </div>
                    <div className="plan-details">
                      <p><strong>Academic Year:</strong> {plan.academic_year}</p>
                      <p><strong>Created:</strong> {new Date(plan.created_at).toLocaleDateString()}</p>
                      <p><strong>Status:</strong> {plan.plan_status}</p>
                    </div>
                    <div className="plan-actions">
                      <button className="action-btn view">👁️ View</button>
                      <button className="action-btn migrate">🚀 Migrate</button>
                      <button className="action-btn delete">🗑️ Delete</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <TimetableOverrideManager 
            tenantId={tenantId} 
            schoolName={schoolName} 
          />
        )}
      </div>
    </div>
  );
};

export default SchoolTimetableSettings;
